import { useState, useEffect } from 'react';
import { settingsApi } from '@/services/api';

// Key used to store WhatsApp number in company_settings table
const WA_NUMBER_KEY = 'whatsapp_default_number';
const WA_ENABLED_KEY = 'whatsapp_enabled';
const WA_TEMPLATE_KEY = 'whatsapp_access_code_template';

interface WhatsAppSettingsTabProps {
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

const DEFAULT_TEMPLATE = `Hello {client_name},

Your Client Portal access code is: *{access_code}*

Please use this code along with your registered email to log in at:
{portal_url}

If you have any questions, feel free to contact us.

Regards,
{company_name}`;

export default function WhatsAppSettingsTab({ onSuccess, onError }: WhatsAppSettingsTabProps) {
  const [waNumber, setWaNumber] = useState('');
  const [waEnabled, setWaEnabled] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testNumber, setTestNumber] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [testMsg, setTestMsg] = useState('');

  useEffect(() => {
    setLoading(true);
    // First try localStorage cache for instant load
    try {
      const cached = localStorage.getItem('erp_whatsapp_settings');
      if (cached) {
        const parsed = JSON.parse(cached) as { number?: string; enabled?: boolean; template?: string };
        if (parsed.number) setWaNumber(parsed.number);
        if (parsed.enabled !== undefined) setWaEnabled(parsed.enabled);
        if (parsed.template) setTemplate(parsed.template);
      }
    } catch { /* ignore */ }
    // Then fetch fresh from API
    settingsApi.getCompanyProfile()
      .then((data: Record<string, string>) => {
        if (data) {
          const num = data[WA_NUMBER_KEY] || data.whatsapp_default_number || '';
          const enabled = data[WA_ENABLED_KEY] === 'true' || data.whatsapp_enabled === 'true';
          const tpl = data[WA_TEMPLATE_KEY] || data.whatsapp_access_code_template || DEFAULT_TEMPLATE;
          setWaNumber(num);
          setWaEnabled(enabled);
          setTemplate(tpl);
          // Update cache
          localStorage.setItem('erp_whatsapp_settings', JSON.stringify({ number: num, enabled, template: tpl }));
        }
      })
      .catch(() => { /* keep defaults */ })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateCompanyProfile({
        [WA_NUMBER_KEY]: waNumber,
        [WA_ENABLED_KEY]: String(waEnabled),
        [WA_TEMPLATE_KEY]: template,
        whatsapp_default_number: waNumber,
        whatsapp_enabled: String(waEnabled),
        whatsapp_access_code_template: template,
      });
      // Persist to localStorage so clients page can read without API call
      localStorage.setItem('erp_whatsapp_settings', JSON.stringify({
        number: waNumber,
        enabled: waEnabled,
        template,
      }));
      // Broadcast so other components can pick up the new number
      window.dispatchEvent(new CustomEvent('whatsapp-settings-updated', {
        detail: { number: waNumber, enabled: waEnabled, template },
      }));
      onSuccess('WhatsApp settings saved successfully');
    } catch {
      onError('Failed to save — check API connection');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = () => {
    if (!testNumber.trim()) return;
    setTestSending(true);
    setTestResult('idle');
    // Build a WhatsApp URL with a sample message
    const sampleMsg = encodeURIComponent(
      template
        .replace('{client_name}', 'Test Client')
        .replace('{access_code}', 'TEST-1234')
        .replace('{portal_url}', window.location.origin + '/client-portal')
        .replace('{company_name}', 'Your Company')
    );
    const cleanNumber = testNumber.replace(/\D/g, '');
    const waUrl = `https://wa.me/${cleanNumber}?text=${sampleMsg}`;
    window.open(waUrl, '_blank');
    setTimeout(() => {
      setTestSending(false);
      setTestResult('success');
      setTestMsg(`WhatsApp opened for ${testNumber}. Message pre-filled with sample access code.`);
    }, 800);
  };

  const formatPreview = (tpl: string) =>
    tpl
      .replace('{client_name}', 'Venkat Rao')
      .replace('{access_code}', 'ABCD-5678')
      .replace('{portal_url}', window.location.origin + '/client-portal')
      .replace('{company_name}', 'CADSoft Survey Solutions');

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center gap-3 text-gray-400">
        <i className="ri-loader-4-line text-xl animate-spin" style={{ color: '#7c3aed' }} />
        <span className="text-sm">Loading WhatsApp settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Enable / Status Banner ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: waEnabled ? '#dcfce7' : '#f9fafb' }}
          >
            <i className="ri-whatsapp-line text-xl" style={{ color: waEnabled ? '#25D366' : '#9ca3af' }} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">WhatsApp Messaging</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {waEnabled
                ? 'WhatsApp is enabled — access codes can be sent directly to clients.'
                : 'WhatsApp is disabled — enable and configure a number to activate.'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setWaEnabled(!waEnabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${waEnabled ? 'bg-green-500' : 'bg-gray-200'}`}
        >
          <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${waEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* ── Default WhatsApp Number ────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f0fdf4' }}>
            <i className="ri-phone-line text-sm" style={{ color: '#25D366' }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Default WhatsApp Number</h3>
            <p className="text-xs text-gray-500">This number will be used as the sender for all WhatsApp messages from the system.</p>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
            WhatsApp Number <span className="text-red-400">*</span>
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">+</span>
              <input
                type="tel"
                value={waNumber}
                onChange={(e) => setWaNumber(e.target.value.replace(/[^0-9+\s\-()]/g, ''))}
                placeholder="91 98765 43210"
                className="w-full pl-7 pr-4 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            Include country code without the + sign. Example: <strong>919876543210</strong> for India (+91 98765 43210)
          </p>
        </div>

        <div className="p-3 rounded-xl border border-green-100 bg-green-50 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
            <i className="ri-information-line text-green-600 text-sm" />
          </span>
          <div className="text-xs text-green-700 space-y-1">
            <p className="font-semibold">How WhatsApp sending works</p>
            <p>When you click &quot;Send via WhatsApp&quot; on a client&apos;s access code, the system opens WhatsApp Web / App with the message pre-filled. The client&apos;s phone number is used as the recipient.</p>
            <p>This uses the <strong>WhatsApp Click-to-Chat</strong> feature — no API key required.</p>
          </div>
        </div>
      </div>

      {/* ── Message Template ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f0fdf4' }}>
            <i className="ri-message-2-line text-sm" style={{ color: '#25D366' }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Access Code Message Template</h3>
            <p className="text-xs text-gray-500">Customize the message sent when sharing a client portal access code.</p>
          </div>
        </div>

        {/* Variables reference */}
        <div className="flex flex-wrap gap-2">
          {['{client_name}', '{access_code}', '{portal_url}', '{company_name}'].map((v) => (
            <span
              key={v}
              onClick={() => setTemplate((t) => t + v)}
              className="text-xs font-mono px-2 py-1 rounded-lg border border-green-200 text-green-700 cursor-pointer hover:bg-green-50 transition-all"
              style={{ background: '#f0fdf4' }}
              title={`Click to insert ${v}`}
            >
              {v}
            </span>
          ))}
          <span className="text-xs text-gray-400 self-center">← click to insert variable</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Template</label>
            <textarea
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={10}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 resize-none font-mono"
            />
            <p className="text-xs text-gray-400 text-right mt-0.5">{template.length}/500</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Preview</label>
            <div
              className="rounded-xl p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed border"
              style={{ background: '#dcfce7', borderColor: '#bbf7d0', minHeight: '200px', fontFamily: 'inherit' }}
            >
              {formatPreview(template)}
            </div>
            <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
              <i className="ri-eye-line" />
              Preview with sample data
            </p>
          </div>
        </div>

        <button
          onClick={() => setTemplate(DEFAULT_TEMPLATE)}
          className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer"
        >
          Reset to default template
        </button>
      </div>

      {/* ── Test Send ──────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f0fdf4' }}>
            <i className="ri-send-plane-line text-sm" style={{ color: '#25D366' }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Test WhatsApp Message</h3>
            <p className="text-xs text-gray-500">Send a test message to verify the template looks correct.</p>
          </div>
        </div>

        {testResult !== 'idle' && (
          <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${testResult === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            <i className={`${testResult === 'success' ? 'ri-checkbox-circle-fill text-green-500' : 'ri-error-warning-fill text-red-500'} mt-0.5 flex-shrink-0`} />
            <div>
              <p className="font-semibold">{testResult === 'success' ? 'WhatsApp opened!' : 'Test failed'}</p>
              <p className="text-xs mt-0.5">{testMsg}</p>
            </div>
          </div>
        )}

        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Send test to (phone number)</label>
            <input
              type="tel"
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder="919876543210"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400"
            />
          </div>
          <button
            disabled={testSending || !testNumber.trim()}
            onClick={handleTestSend}
            className="flex items-center gap-2 text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#25D366' }}
          >
            {testSending
              ? <><i className="ri-loader-4-line animate-spin" />Opening...</>
              : <><i className="ri-whatsapp-line" />Send Test</>}
          </button>
        </div>
      </div>

      {/* ── Save Button ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          disabled={saving}
          onClick={handleSave}
          className="flex items-center gap-2 text-white px-6 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
          style={{ background: '#7c3aed' }}
        >
          {saving
            ? <><i className="ri-loader-4-line animate-spin" />Saving...</>
            : <><i className="ri-save-line" />Save WhatsApp Settings</>}
        </button>
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <i className="ri-database-2-line" />
          Saved to <code className="bg-gray-100 px-1 rounded">company_settings</code> table
        </p>
      </div>
    </div>
  );
}
